import React, { useEffect, useState } from 'react';

import { EVENTS, executeAfterPricingContextEvent, getPcxtV3 } from '@vp/pricing-context-module';
import { Box, FlexBox, FluidImage, Link, SkeletonText, SkeletonTextLine } from '@vp/swan';
import { useLocalizations } from '@vp/ubik-localizations-provider';

import { designServicesContent } from '..';
import { trackDesignServicesCTAClicked } from '../../../infrastructure/clients/client-side/trackingClient';

export interface DesignServicesWrapperProps {
  productKey: string;
  mpvId: string;
  locale: string;
  data: DesignServicesData;
}

export const DesignServicesTile: React.FC<DesignServicesWrapperProps> = (props) => {
  const [isVatInclusiveState, setIsVatInclusive] = useState<boolean | undefined>(undefined);
  const { t } = useLocalizations();

  if (!shouldRender(props.data)) return null;

  useEffect(() => {
    const isVatInitialized: boolean = getPcxtV3()?.isInitialized() ?? false;

    if (isVatInitialized) {
      setIsVatInclusive(getPcxtV3().isVatInclusive());
    } else {
      // Execute on initialization event
      executeAfterPricingContextEvent(
        EVENTS.INITIALIZED_EVENT,
        () => {
          setIsVatInclusive(getPcxtV3()?.isVatInclusive() ?? false);
        },
        false
      );
    }

    // Execute on VAT change event
    executeAfterPricingContextEvent(
      EVENTS.VAT_INCLUSIVITY_CHANGE_EVENT,
      () => {
        setIsVatInclusive(getPcxtV3()?.isVatInclusive() ?? false);
      },
      true
    );
  }, []);

  const designServicesUrl = designServicesContent.buildNextStepUrl(props.mpvId, props.locale);
  const handleTriggerOnClickLink = (): void => {
    trackDesignServicesCTAClicked(props.mpvId, props.productKey, designServicesUrl);
  };

  const formattedPrice = new Intl.NumberFormat(props.locale, {
    style: 'currency',
    currency: props.data.currency,
  }).format(isVatInclusiveState ? props.data.price : props.data.untaxedPrice);

  return (
    <>
      <Box marginBottom="between-subsections">
        {isVatInclusiveState === undefined ? (
          <>
            <Box paddingTop={3} paddingBottom={3}>
              <SkeletonText>
                <SkeletonTextLine></SkeletonTextLine>
              </SkeletonText>
            </Box>
          </>
        ) : (
          <FlexBox
            alignItems="center"
            justifyContent="start"
            flexDirection="row"
            gap={'between-icon-and-text'}
          >
            <Box style={{ width: '80px' }}>
              <FluidImage src={designServicesContent.tileImage} alt="" />
            </Box>

            <Link onClick={handleTriggerOnClickLink} href={designServicesUrl} pt={4} pb={4}>
              {t('let-us-design-it-for-you-from-price', {
                price: formattedPrice,
              })}
            </Link>
          </FlexBox>
        )}
      </Box>
    </>
  );
};

const shouldRender = (designServicesData: DesignServicesData): boolean => {
  return designServicesData.isEnabled;
};

export interface DesignServicesData {
  isEnabled: boolean;
  price: number;
  currency: string;
  untaxedPrice: number;
}
